package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.ProviderKeyDto
import dev.danko.roblox_craft_ai.shared.model.ProviderMetadataDto
import dev.danko.roblox_craft_ai.shared.model.TestKeyResponseV2
import dev.danko.roblox_craft_ai.shared.model.TestKeyStatusV2
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import kotlin.math.ceil

@Composable
fun ProviderCard(
    metadata: ProviderMetadataDto,
    keys: List<ProviderKeyDto>,
    onAddKey: (providerId: String, label: String, apiKey: String, baseUrl: String?) -> Unit = { _, _, _, _ -> },
    onEditKey: (keyId: String, label: String, baseUrl: String?) -> Unit = { _, _, _ -> },
    onDeleteKey: (keyId: String) -> Unit = {},
    onTestKey: suspend (keyId: String) -> TestKeyResponseV2 = { TestKeyResponseV2() },
    onTestAdhoc: suspend (providerId: String, apiKey: String, baseUrl: String?) -> TestKeyResponseV2 = { _, _, _ -> TestKeyResponseV2() },
    onReorderUp: (ProviderKeyDto) -> Unit = {},
    onReorderDown: (ProviderKeyDto) -> Unit = {},
    onToggleEnabled: (keyId: String, isEnabled: Boolean) -> Unit = { _, _ -> },
    modifier: Modifier = Modifier,
    strictSecurityMode: Boolean = false
) {
    val theme = LocalAppTheme.current
    val s = strings()
    val palette = theme.colors
    val scope = rememberCoroutineScope()

    var expanded by remember { mutableStateOf(true) }
    var showAddForm by remember { mutableStateOf(false) }

    var addLabel by remember { mutableStateOf(LocaleController.strings.providerCard.defaultLabel) }
    var addApiKey by remember { mutableStateOf("") }
    var addBaseUrl by remember { mutableStateOf("") }
    var showAddKey by remember { mutableStateOf(false) }
    var isAdding by remember { mutableStateOf(false) }
    var addTestResult by remember { mutableStateOf<TestKeyResponseV2?>(null) }

    var editingKeyId by remember { mutableStateOf<String?>(null) }
    var editLabel by remember { mutableStateOf("") }
    var editBaseUrl by remember { mutableStateOf("") }
    var confirmDeleteKey by remember { mutableStateOf<ProviderKeyDto?>(null) }

    val showSeconds = if (strictSecurityMode) 3 else 10
    val visibleKeyMap = remember { mutableMapOf<String, Int>() }

    LaunchedEffect(Unit) {
        while (true) {
            delay(1000)
            val toRemove = mutableListOf<String>()
            visibleKeyMap.forEach { (k, secs) ->
                if (secs <= 1) toRemove.add(k)
                else visibleKeyMap[k] = secs - 1
            }
            toRemove.forEach { visibleKeyMap.remove(it) }
        }
    }

    AppCard(modifier = modifier.fillMaxWidth(), hoverEnabled = false) {
        Column(Modifier.fillMaxWidth().gap(theme.spacing.sm)) {
            Row(
                modifier = Modifier.fillMaxWidth().cursor(Cursor.Pointer).onClick { expanded = !expanded },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.625.cssRem)) {
                    SpanText(metadata.iconEmoji, Modifier.fontSize(1.5.cssRem))
                    Column(modifier = Modifier.gap(0.15.cssRem)) {
                        SpanText(
                            metadata.displayName,
                            Modifier.fontWeight(FontWeight.Bold).fontSize(1.cssRem).color(palette.textPrimary)
                        )
                        SpanText(
                            s.providerCard.type(metadata.providerType.name),
                            Modifier.fontSize(0.72.cssRem).color(palette.textSecondary)
                        )
                    }
                }
                Spacer()
                val hasActiveKeys = keys.any { it.isEnabled }
                Div(
                    Modifier
                        .padding(topBottom = 2.px, leftRight = 0.6.cssRem)
                        .borderRadius(9999.px)
                        .backgroundColor(
                            if (hasActiveKeys) palette.statusReady.toRgb().copy(alpha = 40)
                            else palette.statusPending.toRgb().copy(alpha = 40)
                        )
                        .toAttrs()
                ) {
                    SpanText(
                        if (hasActiveKeys) s.providerCard.active else s.providerCard.notConfigured,
                        Modifier
                            .color(if (hasActiveKeys) palette.statusReady else palette.statusPending)
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                    )
                }
                SpanText(
                    if (expanded) "▼" else "▶",
                    Modifier.margin(left = 0.5.cssRem).color(palette.textSecondary).fontSize(0.75.cssRem)
                )
            }

            if (expanded) {
                if (keys.isNotEmpty()) {
                    Column(Modifier.fillMaxWidth().gap(theme.spacing.sm)) {
                        keys.sortedBy { it.priority }.forEachIndexed { idx, key ->
                            ProviderKeyRow(
                                key = key,
                                metadata = metadata,
                                visibleSeconds = visibleKeyMap[key.id] ?: 0,
                                onToggleVisible = {
                                    if (visibleKeyMap.containsKey(key.id)) {
                                        visibleKeyMap.remove(key.id)
                                    } else {
                                        visibleKeyMap[key.id] = showSeconds
                                    }
                                },
                                onTest = {
                                    scope.launch { onTestKey(key.id) }
                                },
                                onEdit = {
                                    editingKeyId = key.id
                                    editLabel = key.label
                                    editBaseUrl = key.baseUrl ?: ""
                                },
                                onDelete = { confirmDeleteKey = key },
                                onMoveUp = if (idx > 0) { { onReorderUp(key) } } else null,
                                onMoveDown = if (idx < keys.size - 1) { { onReorderDown(key) } } else null,
                                onToggleEnabled = { onToggleEnabled(key.id, !key.isEnabled) }
                            )
                        }
                    }
                }

                if (!showAddForm) {
                    AppButton(
                        text = s.providerCard.addKeyFor(metadata.displayName),
                        iconLeft = "🔑",
                        variant = AppButtonVariant.PRIMARY,
                        size = AppButtonSize.SM,
                        onClick = { showAddForm = true }
                    )
                } else {
                    val prefixWarn = metadata.apiKeyPatternRegex.isNotBlank() &&
                        addApiKey.isNotBlank() &&
                        !Regex(metadata.apiKeyPatternRegex).matches(addApiKey)

                    Column(Modifier.fillMaxWidth().gap(theme.spacing.sm).padding(theme.spacing.sm)
                        .backgroundColor(palette.surface).borderRadius(theme.radii.md)
                        .border(1.px, LineStyle.Solid, palette.border)) {
                        SpanText(
                            s.providerCard.addKeyTitle(metadata.displayName),
                            Modifier.fontWeight(FontWeight.Bold).fontSize(0.9.cssRem)
                        )
                        AppInput(
                            value = addLabel,
                            onValueChanged = { addLabel = it },
                            label = s.providerCard.labelField,
                            placeholder = s.providerCard.labelPlaceholder,
                            modifier = Modifier.fillMaxWidth()
                        )
                        AppInput(
                            value = addApiKey,
                            onValueChanged = { addApiKey = it },
                            label = s.providerCard.apiKeyField,
                            placeholder = s.providerCard.apiKeyPlaceholder,
                            type = if (showAddKey) InputType.Text else InputType.Password,
                            trailingIcon = {
                                AppButton(
                                    text = if (showAddKey) "🙈" else "👁",
                                    onClick = { showAddKey = !showAddKey },
                                    variant = AppButtonVariant.OUTLINE,
                                    size = AppButtonSize.SM
                                )
                            },
                            modifier = Modifier.fillMaxWidth(),
                            helperText = if (prefixWarn) {
                                s.providerCard.keyPatternWarning(metadata.displayName)
                            } else null,
                            error = if (addApiKey.isNotBlank() && addApiKey.length < 8) s.providerCard.keyTooShort else null
                        )
                        AppInput(
                            value = addBaseUrl,
                            onValueChanged = { addBaseUrl = it },
                            label = s.providerCard.baseUrlOptional,
                            placeholder = s.providerCard.baseUrlDefault(metadata.baseUrlDefault),
                            modifier = Modifier.fillMaxWidth()
                        )

                        if (addTestResult != null) {
                            val r = addTestResult!!
                            val color = when (r.status) {
                                TestKeyStatusV2.VALID -> palette.statusReady
                                TestKeyStatusV2.RATE_LIMIT -> palette.statusPending
                                TestKeyStatusV2.NETWORK_UNKNOWN -> palette.statusFailed
                                TestKeyStatusV2.INVALID -> palette.statusFailed
                                else -> palette.textSecondary
                            }
                            Div(
                                Modifier
                                    .padding(0.6.cssRem)
                                    .borderRadius(theme.radii.sm)
                                    .backgroundColor(color.toRgb().copy(alpha = 25))
                                    .toAttrs()
                            ) {
                                SpanText(
                                    "[${r.status.name}] ${r.message}${r.latencyMs?.let { " (${it}ms)" } ?: ""}",
                                    Modifier.color(color).fontSize(0.8.cssRem)
                                )
                            }
                        }

                        Row(Modifier.fillMaxWidth().gap(theme.spacing.sm)) {
                            AppButton(
                                text = s.providerCard.test,
                                iconLeft = "🔄",
                                variant = AppButtonVariant.OUTLINE,
                                size = AppButtonSize.SM,
                                onClick = {
                                    scope.launch {
                                        addTestResult = onTestAdhoc(
                                            metadata.providerId,
                                            addApiKey,
                                            addBaseUrl.ifBlank { null }
                                        )
                                    }
                                },
                                enabled = addApiKey.length >= 8
                            )
                            Spacer()
                            AppButton(
                                text = s.common.cancel,
                                variant = AppButtonVariant.SECONDARY,
                                size = AppButtonSize.SM,
                                onClick = {
                                    showAddForm = false
                                    addLabel = s.providerCard.defaultLabel
                                    addApiKey = ""
                                    addBaseUrl = ""
                                    addTestResult = null
                                }
                            )
                            AppButton(
                                text = s.common.save,
                                iconLeft = "💾",
                                variant = AppButtonVariant.PRIMARY,
                                size = AppButtonSize.SM,
                                loading = isAdding,
                                onClick = {
                                    if (addApiKey.length < 8 || addLabel.isBlank()) return@AppButton
                                    isAdding = true
                                    scope.launch {
                                        delay(200)
                                        onAddKey(
                                            metadata.providerId,
                                            addLabel,
                                            addApiKey,
                                            addBaseUrl.ifBlank { null }
                                        )
                                        showAddForm = false
                                        addLabel = s.providerCard.defaultLabel
                                        addApiKey = ""
                                        addBaseUrl = ""
                                        addTestResult = null
                                        isAdding = false
                                    }
                                },
                                enabled = addApiKey.length >= 8 && addLabel.isNotBlank()
                            )
                        }
                    }
                }

                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    SpanText("📚 ", Modifier.fontSize(0.75.cssRem))
                    Link(
                        path = metadata.docsUrl,
                        variant = UndecoratedLinkVariant,
                        modifier = Modifier.cursor(Cursor.Pointer).onClick {
                            window.open(metadata.docsUrl, "_blank", "noopener,noreferrer")
                        }
                    ) {
                        SpanText(
                            s.providerCard.getApiKey,
                            Modifier.color(palette.accent).fontSize(0.78.cssRem)
                        )
                    }
                }
            }
        }
    }

    if (editingKeyId != null) {
        val key = keys.firstOrNull { it.id == editingKeyId }
        ModalDialog(
            visible = editingKeyId != null,
            onCloseRequest = { editingKeyId = null },
            title = s.providerCard.editKeyTitle(metadata.displayName),
            footer = {
                Row(Modifier.fillMaxWidth().gap(theme.spacing.sm), horizontalArrangement = Arrangement.End) {
                    AppButton(
                        text = s.common.cancel, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.MD,
                        onClick = { editingKeyId = null }
                    )
                    AppButton(
                        text = s.common.save, iconLeft = "💾",
                        variant = AppButtonVariant.PRIMARY, size = AppButtonSize.MD,
                        onClick = {
                            if (key != null) {
                                onEditKey(key.id, editLabel, editBaseUrl.ifBlank { null })
                            }
                            editingKeyId = null
                        }
                    )
                }
            }
        ) {
            Column(Modifier.fillMaxWidth().gap(theme.spacing.md)) {
                AppInput(
                    value = editLabel,
                    onValueChanged = { editLabel = it },
                    label = s.providerCard.labelShort,
                    modifier = Modifier.fillMaxWidth()
                )
                AppInput(
                    value = editBaseUrl,
                    onValueChanged = { editBaseUrl = it },
                    label = s.providerCard.baseUrlEmptyDefault,
                    placeholder = metadata.baseUrlDefault,
                    modifier = Modifier.fillMaxWidth()
                )
                key?.let { k ->
                    Div(
                        Modifier
                            .fillMaxWidth()
                            .padding(0.75.cssRem)
                            .borderRadius(theme.radii.sm)
                            .backgroundColor(palette.surfaceElevated)
                            .border(1.px, LineStyle.Dashed, palette.border)
                            .toAttrs()
                    ) {
                        SpanText(
                            s.providerCard.keyHiddenNotice,
                            Modifier.color(palette.textSecondary).fontSize(0.78.cssRem)
                        )
                    }
                }
            }
        }
    }

    if (confirmDeleteKey != null) {
        val key = confirmDeleteKey!!
        ModalDialog(
            visible = true,
            onCloseRequest = { confirmDeleteKey = null },
            title = s.providerCard.deleteKeyTitle(key.label),
            dismissOnBackdropClick = false,
            footer = {
                Row(Modifier.fillMaxWidth().gap(theme.spacing.sm), horizontalArrangement = Arrangement.End) {
                    AppButton(
                        text = s.common.cancel, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.MD,
                        onClick = { confirmDeleteKey = null }
                    )
                    AppButton(
                        text = s.common.delete, iconLeft = "🗑",
                        variant = AppButtonVariant.DANGER, size = AppButtonSize.MD,
                        onClick = {
                            onDeleteKey(key.id)
                            confirmDeleteKey = null
                        }
                    )
                }
            }
        ) {
            Column(Modifier.fillMaxWidth().gap(theme.spacing.sm)) {
                SpanText(
                    s.providerCard.deleteIrreversible,
                    Modifier.color(palette.textPrimary).fontSize(0.9.cssRem)
                )
                Div(
                    Modifier
                        .fillMaxWidth()
                        .padding(0.75.cssRem)
                        .borderRadius(theme.radii.sm)
                        .backgroundColor(palette.statusFailed.toRgb().copy(alpha = 20))
                        .border(1.px, LineStyle.Solid, palette.statusFailed.toRgb().copy(alpha = 100))
                        .toAttrs()
                ) {
                    SpanText(
                        s.providerCard.providerKeyLine(metadata.displayName, key.apiKey),
                        Modifier.color(palette.statusFailed).fontSize(0.78.cssRem)
                    )
                }
            }
        }
    }
}

@Composable
private fun ProviderKeyRow(
    key: ProviderKeyDto,
    metadata: ProviderMetadataDto,
    visibleSeconds: Int,
    onToggleVisible: () -> Unit,
    onTest: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onMoveUp: (() -> Unit)?,
    onMoveDown: (() -> Unit)?,
    onToggleEnabled: () -> Unit
) {
    val theme = LocalAppTheme.current
    val s = strings()
    val palette = theme.colors
    val scope = rememberCoroutineScope()
    var isTesting by remember { mutableStateOf(false) }
    var testResp by remember { mutableStateOf<TestKeyResponseV2?>(null) }

    val displayKey = if (visibleSeconds > 0) key.apiKey else {
        if (key.apiKey.length <= 8) "••••••••"
        else "${key.apiKey.take(4)}••••${key.apiKey.takeLast(4)}"
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(0.85.cssRem)
            .backgroundColor(palette.surface)
            .borderRadius(theme.radii.sm)
            .border(1.px, LineStyle.Solid, palette.border),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f).gap(0.25.cssRem)) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                Div(
                    Modifier
                        .padding(leftRight = 0.5.cssRem, topBottom = 0.15.cssRem)
                        .borderRadius(6.px)
                        .backgroundColor(palette.accent.toRgb().copy(alpha = 35))
                        .toAttrs()
                ) {
                    SpanText(key.label, Modifier.color(palette.accent).fontSize(0.72.cssRem).fontWeight(FontWeight.SemiBold))
                }
                val baseUrlValue = key.baseUrl
                if (!baseUrlValue.isNullOrBlank()) {
                    Div(
                        Modifier
                            .padding(leftRight = 0.5.cssRem, topBottom = 0.15.cssRem)
                            .borderRadius(6.px)
                            .backgroundColor(palette.surfaceElevated)
                            .border(1.px, LineStyle.Solid, palette.border)
                            .toAttrs()
                    ) {
                        SpanText(
                            baseUrlValue.replace("https://", "").take(28),
                            Modifier.fontSize(0.7.cssRem).color(palette.textSecondary)
                        )
                    }
                }
                SpanText(
                    "P${key.priority}",
                    Modifier.fontSize(0.65.cssRem).color(palette.textSecondary)
                        .padding(leftRight = 0.35.cssRem, topBottom = 0.1.cssRem)
                        .borderRadius(999.px).backgroundColor(palette.surfaceElevated)
                )
            }
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                SpanText(
                    displayKey,
                    Modifier
                        .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                        .fontSize(0.82.cssRem)
                        .color(palette.textPrimary)
                )
                if (visibleSeconds > 0) {
                    SpanText(
                        s.providerCard.secondsLeft(visibleSeconds),
                        Modifier.fontSize(0.65.cssRem).color(palette.statusPending)
                    )
                }
            }
            testResp?.let { r ->
                val c = when (r.status) {
                    TestKeyStatusV2.VALID -> palette.statusReady
                    TestKeyStatusV2.INVALID -> palette.statusFailed
                    TestKeyStatusV2.RATE_LIMIT -> palette.statusPending
                    else -> palette.textSecondary
                }
                SpanText(
                    "[${r.status}] ${r.message.take(60)}",
                    Modifier.fontSize(0.7.cssRem).color(c)
                )
            }
        }

        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.25.cssRem)) {
            AppButton(
                text = "", iconLeft = if (visibleSeconds > 0) "🙈" else "👁",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                onClick = {
                    scope.launch {
                        onToggleVisible()
                    }
                }
            )
            AppButton(
                text = "", iconLeft = "📋",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                onClick = {
                    window.navigator.asDynamic().clipboard?.writeText(
                        if (visibleSeconds > 0) key.apiKey else "••••••••"
                    )
                }
            )
            AppButton(
                text = "", iconLeft = "🔄",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                loading = isTesting,
                onClick = {
                    isTesting = true
                    scope.launch {
                        val t = kotlinx.coroutines.withTimeoutOrNull(15000L) {
                            onTest()
                            TestKeyResponseV2()
                        }
                        testResp = t
                        isTesting = false
                        delay(5000)
                        testResp = null
                    }
                }
            )
            AppButton(
                text = "", iconLeft = "✏️",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                onClick = onEdit
            )
            AppButton(
                text = "", iconLeft = "🗑",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                onClick = onDelete
            )
            Div(Modifier.width(1.px).height(1.6.cssRem).backgroundColor(palette.border).margin(leftRight = 0.25.cssRem).toAttrs())
            AppButton(
                text = "", iconLeft = "▲",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                enabled = onMoveUp != null,
                onClick = { onMoveUp?.invoke() }
            )
            AppButton(
                text = "", iconLeft = "▼",
                variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                enabled = onMoveDown != null,
                onClick = { onMoveDown?.invoke() }
            )
            Div(Modifier.width(1.px).height(1.6.cssRem).backgroundColor(palette.border).margin(leftRight = 0.25.cssRem).toAttrs())
            SpanText(
                text = if (key.isEnabled) "✓ON" else "✗OFF",
                modifier = Modifier
                    .padding(leftRight = 0.5.cssRem, topBottom = 0.18.cssRem)
                    .borderRadius(6.px)
                    .backgroundColor(
                        if (key.isEnabled) palette.statusReady.toRgb().copy(alpha = 30)
                        else palette.surfaceElevated
                    )
                    .fontSize(0.68.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(if (key.isEnabled) palette.statusReady else palette.textSecondary)
                    .cursor(Cursor.Pointer)
                    .onClick { onToggleEnabled() }
            )
        }
    }
}
